import { z } from "zod";

import { busbarMaterialSchema } from "./busbarEvidenceContract";
import { faultTypeSchema, shortCircuitCaseSchema } from "./faultContract";
import { prospectiveCurrentSourceSchema } from "./starterCoordinationContract";

// The controlled vocabularies of the LV busbar sizing and material selection (EOS-07c,
// Master Prompt A29), copied verbatim from the backend so a value the API can send is never
// rejected here. Sources at commit 3f0c017, all under backend/app/domain/electrical/:
// busbar_selection/models.py (SystemKind, VoltageDropBasis, MaterialChoice,
// VerificationOutcome, TemperatureBasis, TemperatureRiseMethod, REFERENCE_PARTS) and
// busbar_selection/results.py (CheckKey, CheckStatus, BusbarSelectionStatus,
// BusbarSelectionWarningCode).
// busbarSelectionContract.test.ts pins each list.
//
// The busbar material (busbar_evidence/models.py), the prospective current source
// (fault_link.py), the calculation case and the fault type of the linked fault run are shared
// enums, so they are reused rather than written out a second time.

export const systemKindSchema = z.enum(["PANEL_BUSBAR", "BUSWAY"]);

export const voltageDropBasisSchema = z.enum(["LINE_TO_LINE", "LINE_TO_NEUTRAL"]);

// The material asked for; BOTH compares copper and aluminium (A29 (l)).
export const materialChoiceSchema = z.enum(["COPPER", "ALUMINIUM", "BOTH"]);

export const verificationOutcomeSchema = z.enum(["VERIFIED", "FAILED"]);

export const temperatureBasisSchema = z.enum(["RISE", "ABSOLUTE"]);

// The three parts of a document reference; a value is verified only with all three
// (founder rule 2026-10-01, follow-up 21).
export const referencePartSchema = z.enum(["document", "revision_or_date", "page_or_clause"]);

export const temperatureRiseMethodSchema = z.enum([
  "TEST",
  "COMPARISON",
  "CALCULATION",
  "MANUFACTURER_VERIFIED_DESIGN",
]);

// The checks of KES-EL-BUS-002, in the order the backend records them.
export const checkKeySchema = z.enum([
  "screening",
  "continuous_current",
  "temperature_rise",
  "short_time_withstand",
  "peak_withstand",
  "neutral",
  "protective_earth",
  "clearance",
  "voltage_drop",
  "supports",
]);

export const checkStatusSchema = z.enum([
  "VALID",
  "REVIEW_REQUIRED",
  "NO_SOLUTION",
  "NOT_APPLICABLE",
]);

// Never VALID: the ratings are declared, not proven (A16 (d)).
export const busbarSelectionStatusSchema = z.enum(["WARNING", "REVIEW_REQUIRED", "NO_SOLUTION"]);

export const busbarSelectionWarningCodeSchema = z.enum([
  "PROSPECTIVE_CURRENT_NOT_LINKED",
  "FAULT_RUN_NOT_MAXIMUM_THREE_PHASE",
  "FAULT_RUN_BUS_MISMATCH",
  "MARGIN_FACTOR_UNUSUALLY_LARGE",
  "EVIDENCE_REFERENCE_INCOMPLETE",
  "BUSBAR_SELECTION_MANUFACTURER_DECLARED",
]);

export { busbarMaterialSchema, faultTypeSchema, prospectiveCurrentSourceSchema, shortCircuitCaseSchema };

export type SystemKind = z.infer<typeof systemKindSchema>;
export type VoltageDropBasis = z.infer<typeof voltageDropBasisSchema>;
export type MaterialChoice = z.infer<typeof materialChoiceSchema>;
export type VerificationOutcome = z.infer<typeof verificationOutcomeSchema>;
export type TemperatureBasis = z.infer<typeof temperatureBasisSchema>;
export type TemperatureRiseMethod = z.infer<typeof temperatureRiseMethodSchema>;
export type ReferencePart = z.infer<typeof referencePartSchema>;
export type CheckKey = z.infer<typeof checkKeySchema>;
export type CheckStatus = z.infer<typeof checkStatusSchema>;
export type BusbarSelectionStatus = z.infer<typeof busbarSelectionStatusSchema>;
export type BusbarSelectionWarningCode = z.infer<typeof busbarSelectionWarningCodeSchema>;
